import { CableId, getCatalogItem, isJunctionBox } from "@/shared/catalog";
import type { Pt } from "@/shared/lib/geometry";
import type { Cable, ElectricalGroup, ElectricalGroupPurpose, PlanLayers } from "@/shared/plan";
import { elbowTo } from "@/widgets/plan";
import { routeWallCrossings, routesNear } from "./wiringWarnings";

export type AutoTargetKind = "outlets" | "lighting" | "dedicated" | "threePhase" | "network" | "tv";
export interface AutoTarget {
	id: string;
	point: Pt;
	roomId: string;
	roomName: string;
	kind: AutoTargetKind;
}
export interface AutoSource {
	id: string;
	point: Pt;
	kind: "power" | "lowVoltage";
	phases?: 1 | 3;
	placement?: AutoBox;
}
export interface AutoBox {
	wallId: string;
	side: 1 | -1;
	offset: number;
	elevation: number;
	point: Pt;
	ceiling?: boolean;
	existingId?: string;
	targetIds?: string[];
}
export interface AutoCable {
	from: string;
	to: string;
	points: Pt[];
	route: Pt[];
}
export interface AutoGroup {
	id: string;
	name: string;
	purpose: ElectricalGroupPurpose;
	sourceId: string;
	sourceKind: AutoSource["kind"];
	material: string;
	targets: AutoTarget[];
	box?: AutoBox;
	boxId?: string;
	cables: AutoCable[];
}
export interface AutoWiringPlan {
	groups: AutoGroup[];
	warnings: string[];
}

export const autoWiringExistingCables = (cables: Record<string, Cable>, rebuild: boolean) =>
	rebuild ? {} : cables;

export const matchingAutoGroup = (
	groups: Record<string, ElectricalGroup>,
	group: Omit<ElectricalGroup, "id">,
	used: ReadonlySet<string>,
) =>
	Object.values(groups).find(
		(existing) =>
			!used.has(existing.id) &&
			existing.name === group.name &&
			existing.purpose === group.purpose &&
			existing.panelId === group.panelId,
	)?.id;

export const unusedWiringGroups = (
	groups: Record<string, ElectricalGroup>,
	cables: Record<string, Cable>,
) => {
	const used = new Set(
		Object.values(cables).flatMap((cable) => (cable.groupId ? [cable.groupId] : [])),
	);
	return Object.keys(groups).filter((id) => !used.has(id));
};

export const unusedWiringBoxes = (layers: PlanLayers, includeManual: boolean) => {
	const protectedIds = new Set([
		...Object.values(layers.wiring.items).flatMap((cable) => [cable.from, cable.to]),
		...Object.values(layers.wiring.connections).flatMap((connection) => [
			connection.from,
			connection.to,
		]),
		...Object.values(layers.wiring.groups).flatMap((group) =>
			group.panelId ? [group.panelId] : [],
		),
		...Object.values(layers.comments.items).flatMap((comment) =>
			comment.target ? [comment.target.id] : [],
		),
	]);
	const fixtures = Object.values(layers.electrical.fixtures).filter(
		(item) =>
			includeManual && !protectedIds.has(item.id) && isJunctionBox(getCatalogItem(item.ref)),
	);
	const ceilingItems = Object.values(layers.electrical.items).filter(
		(item) =>
			(includeManual || item.autoWiring) &&
			!protectedIds.has(item.id) &&
			isJunctionBox(getCatalogItem(item.ref)),
	);
	return {
		fixtures: fixtures.map((item) => item.id),
		ceilingItems: ceilingItems.map((item) => item.id),
	};
};

export const autoEndpointLabels = (endpoints: { id: string; name: string }[]) => {
	const totals = new Map<string, number>();
	const numbers = new Map<string, number>();
	for (const endpoint of endpoints) totals.set(endpoint.name, (totals.get(endpoint.name) ?? 0) + 1);
	return new Map(
		endpoints.map(({ id, name }) => {
			const number = (numbers.get(name) ?? 0) + 1;
			numbers.set(name, number);
			return [id, totals.get(name)! > 1 ? `${name} ${number}` : name];
		}),
	);
};

const materialFor = (kind: AutoTargetKind): string => {
	if (kind === "lighting") return CableId.Vvg3x15;
	if (kind === "threePhase") return CableId.Vvg5x25;
	if (kind === "network") return CableId.Utp;
	if (kind === "tv") return CableId.Coaxial;
	return CableId.Vvg3x25;
};

const connect = (from: string, start: Pt, to: string, end: Pt): AutoCable => {
	const points = elbowTo(null, start, end);
	return { from, to, points, route: [start, ...points, end] };
};

const distance = (left: Pt, right: Pt) => Math.abs(left.x - right.x) + Math.abs(left.y - right.y);

const betterRouteCost = (
	left: { crossings: number; near: number; length: number },
	right: { crossings: number; near: number; length: number },
) =>
	left.crossings < right.crossings ||
	(left.crossings === right.crossings &&
		(left.near < right.near || (left.near === right.near && left.length < right.length)));

export const suggestLowVoltageSource = (
	targets: AutoTarget[],
	sources: AutoSource[],
	candidates: AutoBox[],
): AutoSource | undefined => {
	const lowTargets = targets.filter((target) => target.kind === "network" || target.kind === "tv");
	if (!lowTargets.length || sources.some((source) => source.kind === "lowVoltage"))
		return undefined;
	const panels = sources.filter((source) => source.kind === "power");
	const score = (candidate: AutoBox) =>
		panels.length
			? Math.min(...panels.map((panel) => distance(candidate.point, panel.point)))
			: lowTargets.reduce((sum, target) => sum + distance(candidate.point, target.point), 0);
	const placement = candidates.toSorted((left, right) => score(left) - score(right))[0];
	return placement
		? { id: "draft-low-voltage-source", point: placement.point, kind: "lowVoltage", placement }
		: undefined;
};

export const autoCableTurn = (
	cable: AutoCable,
	direction: "horizontal" | "vertical",
): AutoCable => {
	const start = cable.route[0];
	const end = cable.route[cable.route.length - 1];
	const corner = direction === "horizontal" ? { x: end.x, y: start.y } : { x: start.x, y: end.y };
	const points = start.x === end.x || start.y === end.y ? [] : [corner];
	return { ...cable, points, route: [start, ...points, end] };
};

export const overlappingRouteLength = (first: Pt[], second: Pt[]): number => {
	let total = 0;
	for (let index = 1; index < first.length; index++) {
		const start = first[index - 1];
		const end = first[index];
		for (let otherIndex = 1; otherIndex < second.length; otherIndex++) {
			const otherStart = second[otherIndex - 1];
			const otherEnd = second[otherIndex];
			if (start.y === end.y && otherStart.y === otherEnd.y && start.y === otherStart.y)
				total += Math.max(
					0,
					Math.min(Math.max(start.x, end.x), Math.max(otherStart.x, otherEnd.x)) -
						Math.max(Math.min(start.x, end.x), Math.min(otherStart.x, otherEnd.x)),
				);
			else if (start.x === end.x && otherStart.x === otherEnd.x && start.x === otherStart.x)
				total += Math.max(
					0,
					Math.min(Math.max(start.y, end.y), Math.max(otherStart.y, otherEnd.y)) -
						Math.max(Math.min(start.y, end.y), Math.min(otherStart.y, otherEnd.y)),
				);
		}
	}
	return total;
};

export const routeThroughDoors = (
	cable: AutoCable,
	walls: { start: Pt; end: Pt; doors?: { from: number; to: number }[] }[],
	otherRoutes: Pt[][] = [],
): AutoCable => {
	const start = cable.route[0];
	const end = cable.route[cable.route.length - 1];
	const doorCount = walls.reduce((count, wall) => count + (wall.doors?.length ?? 0), 0);
	const fractions = otherRoutes.length && doorCount <= 18 ? [0.2, 0.5, 0.8] : [0.5];
	const portals = walls.flatMap((wall) =>
		(wall.doors ?? []).flatMap((door) =>
			fractions.map((fraction) => {
				const offset = door.from + (door.to - door.from) * fraction;
				return {
					x: wall.start.x + (wall.end.x - wall.start.x) * offset,
					y: wall.start.y + (wall.end.y - wall.start.y) * offset,
				};
			}),
		),
	);
	const nodes = [start, end, ...portals];
	const costs = nodes.map(() => ({ crossings: Infinity, near: Infinity, length: Infinity }));
	const routes: Pt[][] = nodes.map(() => []);
	const visited = new Set<number>();
	costs[0] = { crossings: 0, near: 0, length: 0 };
	routes[0] = [start];
	for (let iteration = 0; iteration < nodes.length; iteration++) {
		let current = -1;
		for (let index = 0; index < nodes.length; index++)
			if (!visited.has(index) && (current < 0 || betterRouteCost(costs[index], costs[current])))
				current = index;
		if (current < 0 || current === 1) break;
		visited.add(current);
		for (let next = 0; next < nodes.length; next++) {
			if (visited.has(next) || next === current) continue;
			const first = nodes[current];
			const last = nodes[next];
			const middleX = (first.x + last.x) / 2;
			const middleY = (first.y + last.y) / 2;
			for (const points of [
				[{ x: last.x, y: first.y }],
				[{ x: first.x, y: last.y }],
				[
					{ x: middleX, y: first.y },
					{ x: middleX, y: last.y },
				],
				[
					{ x: first.x, y: middleY },
					{ x: last.x, y: middleY },
				],
			]) {
				const route = [first, ...points, last];
				const crossings =
					routeWallCrossings(route, walls).length +
					walls.filter((wall) => overlappingRouteLength(route, [wall.start, wall.end]) > 1).length;
				const candidate = {
					crossings: costs[current].crossings + crossings,
					near:
						costs[current].near + otherRoutes.filter((other) => routesNear(route, other)).length,
					length: costs[current].length + distance(first, last),
				};
				if (betterRouteCost(candidate, costs[next])) {
					costs[next] = candidate;
					routes[next] = [...routes[current], ...points, last];
				}
			}
		}
	}
	const route = routes[1].filter(
		(point, index, all) =>
			index === 0 || point.x !== all[index - 1].x || point.y !== all[index - 1].y,
	);
	return route.length > 1 ? { ...cable, points: route.slice(1, -1), route } : cable;
};

export const chooseAutoCableRoute = (
	cable: AutoCable,
	walls: { start: Pt; end: Pt; doors?: { from: number; to: number }[] }[],
	otherRoutes: Pt[][],
	parallelRoutes: Pt[][] = otherRoutes,
): AutoCable => {
	const initialRoute = routeThroughDoors(cable, walls);
	const throughDoors = otherRoutes.some((route) => routesNear(initialRoute.route, route))
		? routeThroughDoors(cable, walls, otherRoutes)
		: initialRoute;
	const choices = [
		throughDoors,
		cable,
		autoCableTurn(cable, "horizontal"),
		autoCableTurn(cable, "vertical"),
	];
	const start = cable.route[0];
	const end = cable.route[cable.route.length - 1];
	for (const shiftX of [-360, -240, -120, 120, 240, 360]) {
		for (const shiftY of [-shiftX, shiftX]) {
			const shifted = throughDoors.route.map((point) => ({
				x: point.x + shiftX,
				y: point.y + shiftY,
			}));
			const shiftedStart = shifted[0];
			const shiftedEnd = shifted[shifted.length - 1];
			for (const horizontalFirst of [true, false]) {
				const route = [
					start,
					horizontalFirst ? { x: shiftedStart.x, y: start.y } : { x: start.x, y: shiftedStart.y },
					...shifted,
					horizontalFirst ? { x: shiftedEnd.x, y: end.y } : { x: end.x, y: shiftedEnd.y },
					end,
				].filter(
					(point, index, all) =>
						index === 0 || point.x !== all[index - 1].x || point.y !== all[index - 1].y,
				);
				choices.push({ ...cable, points: route.slice(1, -1), route });
			}
		}
	}
	for (const offset of [-240, -120, 0, 120, 240]) {
		const middleX = Math.round((start.x + end.x) / 2 + offset);
		const middleY = Math.round((start.y + end.y) / 2 + offset);
		for (const points of [
			[
				{ x: middleX, y: start.y },
				{ x: middleX, y: end.y },
			],
			[
				{ x: start.x, y: middleY },
				{ x: end.x, y: middleY },
			],
		]) {
			const route = [start, ...points, end].filter(
				(point, index, all) =>
					index === 0 || point.x !== all[index - 1].x || point.y !== all[index - 1].y,
			);
			choices.push({ ...cable, points: route.slice(1, -1), route });
		}
	}
	const score = (choice: AutoCable) => [
		routeWallCrossings(choice.route, walls).length +
			walls.filter((wall) => overlappingRouteLength(choice.route, [wall.start, wall.end]) > 1)
				.length,
		otherRoutes.filter((route) => routesNear(choice.route, route)).length,
		parallelRoutes.reduce((sum, route) => sum + overlappingRouteLength(choice.route, route), 0),
		choice.route
			.slice(1)
			.reduce((sum, point, index) => sum + distance(choice.route[index], point), 0),
	];
	return choices.reduce((best, candidate) => {
		const candidateScore = score(candidate);
		const bestScore = score(best);
		for (let index = 0; index < candidateScore.length; index++) {
			if (candidateScore[index] < bestScore[index]) return candidate;
			if (candidateScore[index] > bestScore[index]) return best;
		}
		return best;
	});
};

export const planAutoWiring = (
	targets: AutoTarget[],
	sources: AutoSource[],
	boxesByRoom: Record<string, AutoBox[]>,
	selectedSources: Record<string, string> = {},
	excludedTargets: ReadonlySet<string> = new Set(),
): AutoWiringPlan => {
	const warnings: string[] = [];
	const buckets = new Map<string, AutoTarget[]>();
	for (const target of targets) {
		const key = `${target.roomId}:${target.kind}:${target.kind === "dedicated" || target.kind === "threePhase" ? target.id : ""}`;
		buckets.set(key, [...(buckets.get(key) ?? []), target]);
	}
	const groups: AutoGroup[] = [];
	const usedBoxes: AutoBox[] = [];
	let groupIndex = 0;
	for (const bucket of buckets.values()) {
		const first = bucket[0];
		const id = `draft-group-${groupIndex++}`;
		const sourceKind = first.kind === "network" || first.kind === "tv" ? "lowVoltage" : "power";
		const candidates = sources.filter((candidate) => candidate.kind === sourceKind);
		const source =
			candidates.find((candidate) => candidate.id === selectedSources[id]) ??
			(candidates.length === 1 ? candidates[0] : undefined);
		if (!candidates.length) {
			warnings.push(
				`${first.roomName}: нет ${sourceKind === "power" ? "электрощита" : "слаботочного узла"} для ${bucket.length} приборов; подключите их вручную.`,
			);
			continue;
		}
		if (!source) warnings.push(`${first.roomName}: выберите источник группы.`);
		if (first.kind === "threePhase" && source && source.phases !== 3)
			warnings.push(`${first.roomName}: трёхфазное питание щитка не подтверждено.`);
		const activeTargets = bucket.filter((candidate) => !excludedTargets.has(candidate.id));
		const hasExistingBox = boxesByRoom[first.roomId]?.some(
			(candidate) =>
				candidate.existingId &&
				candidate.targetIds?.some((targetId) =>
					activeTargets.some((target) => target.id === targetId),
				),
		);
		const needsBox =
			!!hasExistingBox ||
			(activeTargets.length > 1 && (first.kind === "lighting" || first.kind === "outlets"));
		const cost = (candidate: AutoBox) =>
			activeTargets.reduce(
				(sum, target) => sum + distance(candidate.point, target.point),
				source ? distance(candidate.point, source.point) : 0,
			);
		const box = needsBox
			? boxesByRoom[first.roomId]
					?.filter(
						(candidate) =>
							(!candidate.existingId ||
								candidate.targetIds?.some((id) =>
									activeTargets.some((target) => target.id === id),
								)) &&
							usedBoxes.every(
								(used) =>
									Math.hypot(candidate.point.x - used.point.x, candidate.point.y - used.point.y) >=
									200,
							),
					)
					.toSorted(
						(left, right) =>
							Number(!!right.existingId) - Number(!!left.existingId) || cost(left) - cost(right),
					)[0]
			: undefined;
		if (box) usedBoxes.push(box);
		else if (needsBox)
			warnings.push(
				`${first.roomName}: не найдено места для распредкоробки, предложены прямые линии.`,
			);
		const boxId = box ? `draft-box-${id}` : undefined;
		const cables =
			source && box && boxId && activeTargets.length
				? [connect(source.id, source.point, boxId, box.point)]
				: [];
		if (source)
			for (const target of activeTargets)
				cables.push(
					connect(boxId ?? source.id, box?.point ?? source.point, target.id, target.point),
				);
		const labels: Record<AutoTargetKind, string> = {
			outlets: "Розетки",
			lighting: "Освещение",
			dedicated: "Отдельный прибор",
			threePhase: "Трёхфазный прибор",
			network: "LAN",
			tv: "ТВ",
		};
		groups.push({
			id,
			name: `${labels[first.kind]}: ${first.roomName}`,
			purpose: first.kind === "threePhase" ? "dedicated" : first.kind,
			sourceId: source?.id ?? "",
			sourceKind,
			material: materialFor(first.kind),
			targets: bucket,
			box,
			boxId,
			cables,
		});
	}
	return { groups, warnings };
};
